<script setup>
import { ref } from 'vue'
import { getI18n, getTranslatedField } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'
import { useLookup, useDefaultDialogTitle } from '@/composables/data-grid'

import { DxTreeList } from 'devextreme-vue'

import { useGarageStore } from '@/stores/garage.store'

import CustomToolbarResetSettingsButtonTemplate from '@/components/shared/CustomToolbarResetSettingsButtonTemplate.vue'

const props = defineProps({
  readOnly: { type: Boolean }
})

const gridId = 'intervention-type-grid'
const gridRef = ref(null)
const i18n = getI18n()

const garageStore = useGarageStore()

const columns = [
  { dataField: 'code', caption: i18n.t('common.code'), validationRules: [{ type: 'required' }] },
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    calculateCellValue: (rowData) => getTranslatedField(rowData, 'name'),
    validationRules: [{ type: 'required' }]
  },
  {
    dataField: 'color',
    caption: i18n.t('common.color'),
    cellTemplate: 'colorCellTemplate',
    validationRules: [{ type: 'required' }]
  },
  {
    dataField: 'type',
    caption: i18n.t('common.type'),
    lookup: useLookup({ store: garageStore.getLocalizedDocumentProgressStatuses().filter(s => s.order === 0), valueExpr: 'type' }),
    validationRules: [{ type: 'required' }]
  },
  {
    dataField: 'order',
    caption: i18n.t('common.order'),
    validationRules: [{ type: 'required' }]
  },
  {
    dataField: 'visible',
    caption: i18n.t('common.view'),
  },
]

const {defaultDialogTitle} = useDefaultDialogTitle()
const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.interventionType')), {
  itemType: 'group',
  colCount: 1,
  items: [
    {
      itemType: 'group',
      colCount: 2,
      items: [
        { dataField: 'code', colSpan: 1, editorOptions: { maxLength: 6 } },
        { dataField: 'name', colSpan: 1 },
        { dataField: 'color', colSpan: 1, editorType: 'dxColorBox' },
        { dataField: 'type', colSpan: 1, editorOptions: { readOnly: true } },
        { dataField: 'order', colSpan: 1, editorOptions: { min: 0 } },
        { dataField: 'visible', colSpan: 1, editorType: 'dxCheckBox' },
      ]
    }
  ]
}, {
  allowAdding: false,
  allowUpdating: !props.readOnly,
  allowDeleting: (rowData) => rowData.row?.data?.order > 0 && !props.readOnly,
})

const onEditorPreparing = (e) => {
  if (e.parentType === 'dataRow' && (e.dataField === 'code' || e.dataField === 'order')) {
    e.editorOptions.readOnly = e.row?.data?.order === 0
  }

  if (e.parentType === 'dataRow' && e.dataField === 'visible') {
    e.editorOptions.readOnly = true
  }
}

const onSaving = async(e) => {
  e.cancel = true
  if (!e.changes.length) return

  const change = e.changes[0]
  if (change.type === 'remove') {
    await garageStore.api.deleteDocumentProgressStatusByIdAsync(change.key)
  } else if (!isNaN(change.key)) {
    await garageStore.api.updateDocumentProgressStatusAsync(change.key, change.data)
  } else {
    await garageStore.api.createDocumentProgressStatusAsync(change.data)
  }

  await garageStore.fetchDocumentProgressStatusesAsync()
  e.component.cancelEditData()
  e.component.refresh()
}

const onInitNewRow = (e) => {
  if (e.data.parentId) {
    const dataSource = e.component.getDataSource()
    const parent = dataSource.items().filter(item => item.key === e.data.parentId)[0]

    const maxOrder = parent.children.length > 0 ? Math.max(...parent.children.map(item => item.data.order || 0)) : 0

    e.data.type = parent.data.type ?? null
    e.data.order = maxOrder + 1
  }
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, {
    allowResetSettings: true,
    addButtonOptions: { visible: false, disabled: true },
    useDefaultAddButton: true,
  })
}

const dataSource = dataGrid.useODataSource(garageStore.api.getAbsoluteUrl() + `${garageStore.api.documentProgressStatusesUrl}grouped/`, {avoidDuplicateDatasourceIds: true})
const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  keyExpr: 'id',
  dataSource: dataSource,
  columns: columns,
  autoExpandAll: false,
  pager: {
    visible: false
  },
  remoteOperations: {
    filtering: false,
    sorting: false,
    paging: false
  },
  editing: editing,
  columnChooser: { enabled: false },
  filterRow: { visible: !props.readOnly },
  itemsExpr: 'items',
  dataStructure: 'tree',

  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onToolbarPreparing: onToolbarPreparing,
  onSaving: onSaving,
  height: '100%',
})
</script>
<template>
  <dx-tree-list
    ref="gridRef"
    v-bind="gridOptions"
  >
    <template #customToolbarResetSettingsButtonTemplate>
      <custom-toolbar-reset-settings-button-template
        :grid="gridRef"
        :visible="!props.readOnly"
        :on-reset="garageStore.api.resetSettingsDocumentProgressStatusAsync"
      />
    </template>
    <template #colorCellTemplate="{ data }">
      <div style="display: flex; align-items: center; gap: 8px;">
        <div
          :style="{
            width: '16px',
            height: '16px',
            backgroundColor: data.data.color,
            border: '1px solid #ccc',
          }"
        />
        <span>{{ data.data.color }}</span>
      </div>
    </template>
  </dx-tree-list>
</template>
